import "./styles.scss";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { UIEvent } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useUserContext } from "../../hooks/userContext";
import DashboardHeader from "../../components/dashboard-header";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  GET_MESSAGE_THREADS,
  GET_MESSAGE_THREAD_MESSAGES,
  MARK_MESSAGE_THREAD_READ,
  SEND_MESSAGE,
  TOGGLE_MESSAGE_REACTION,
} from "../../graphql/messages";
import { createMessageSocket } from "../../services/socket";
import { useFirebaseFileUpload } from "../../hooks/useFirebaseFileUpload";
import { FirebasePaths } from "../../utils/firebase";
import type { MediaFile } from "../../__generated__/types";
import { MESSAGE_PAGE_SIZE } from "./messages-constants";
import {
  getMessageThreadId,
  getMessageTimestampValue,
  getOtherParticipant,
  parseDateValue,
} from "./messages-utils";
import MessagesSidebar from "./components/MessagesSidebar";
import MessagesThreadHeader from "./components/MessagesThreadHeader";
import MessagesThreadBody from "./components/MessagesThreadBody";
import MessageComposer from "./components/MessageComposer";
import MessagesImageModal from "./components/MessagesImageModal";
import { fetchMessagePermission } from "../../services/matchesApi";
import { useNotification } from "../../contexts/NotificationContext";

const MESSAGE_PERMISSION_CHECK_FAILED_REASON =
  "Unable to verify messaging permission right now. Please try again.";

export const MessagesPage = () => {
  const { user } = useUserContext();
  const { showError } = useNotification();
  const role = user?.accountType || "";
  const isMobile = useMediaQuery("(max-width: 960px)");
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [messageList, setMessageList] = useState<any[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<MediaFile[]>([]);
  const [replyTarget, setReplyTarget] = useState<any | null>(null);
  const [reactionPickerForId, setReactionPickerForId] = useState<string | null>(
    null
  );
  const [imagePreview, setImagePreview] = useState<{
    src: string;
    title?: string;
  } | null>(null);
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(
    null
  );
  const threadBodyRef = useRef<HTMLDivElement | null>(null);
  const autoScrollThreadIdRef = useRef<string | null>(null);
  const historyLoadModeRef = useRef<"replace" | "prepend">("replace");
  const requestedThreadIdRef = useRef<string | null>(null);
  const pendingScrollAdjustmentRef = useRef<{
    height: number;
    top: number;
  } | null>(null);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [messagePermission, setMessagePermission] = useState<{
    allowed: boolean;
    reason: string | null;
    loading: boolean;
  }>({ allowed: true, reason: null, loading: false });
  const selectedThreadIdRef = useRef<string | null>(null);
  const { onUpload: uploadFiles, loading: uploadingFiles } =
    useFirebaseFileUpload(FirebasePaths.MESSAGES);

  const {
    data: threadsData,
    loading: threadsLoading,
    refetch: refetchThreads,
  } = useQuery(GET_MESSAGE_THREADS, {
    variables: { userId: user?.id, role },
    skip: !user?.id || !role,
    fetchPolicy: "cache-and-network",
  });

  const [loadMessages, { data: messagesData, loading: messagesLoading }] =
    useLazyQuery(GET_MESSAGE_THREAD_MESSAGES, {
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
    });

  const [sendMessage, { loading: sendingMessage }] = useMutation(SEND_MESSAGE);
  const [markThreadRead] = useMutation(MARK_MESSAGE_THREAD_READ);
  const [toggleMessageReaction] = useMutation(TOGGLE_MESSAGE_REACTION);

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  useEffect(() => {
    const container = document.getElementById("dashboard-content");
    if (!container) return;
    container.classList.add("dashboard-content--messages");
    return () => {
      container.classList.remove("dashboard-content--messages");
    };
  }, []);

  useEffect(() => {
    autoScrollThreadIdRef.current = null;
  }, [selectedThreadId]);

  const fetchThreadMessages = useCallback(
    ({
      threadId,
      limit = MESSAGE_PAGE_SIZE,
      before = null,
      mode = "replace",
    }: {
      threadId: string;
      limit?: number;
      before?: string | null;
      mode?: "replace" | "prepend";
    }) => {
      requestedThreadIdRef.current = threadId;
      historyLoadModeRef.current = mode;
      if (mode === "prepend") {
        const container = threadBodyRef.current;
        if (container) {
          pendingScrollAdjustmentRef.current = {
            height: container.scrollHeight,
            top: container.scrollTop,
          };
        }
        setIsFetchingOlder(true);
      } else {
        setIsFetchingOlder(false);
      }
      loadMessages({
        variables: {
          threadId,
          limit,
          ...(before ? { before } : {}),
        },
      });
    },
    [loadMessages]
  );

  useEffect(() => {
    if (!selectedThreadId) {
      setMessageList([]);
    }
    setPendingAttachments([]);
    setReplyTarget(null);
    setReactionPickerForId(null);
    setImagePreview(null);
    setScrollTargetId(null);
    setHighlightedMessageId(null);
    setIsFetchingOlder(false);
    setHasMoreHistory(true);
    historyLoadModeRef.current = "replace";
    requestedThreadIdRef.current = null;
    pendingScrollAdjustmentRef.current = null;
  }, [selectedThreadId]);

  useEffect(() => {
    if (!isMobile) return;
    if (!selectedThreadId) {
      setMobileView("list");
    }
  }, [isMobile, selectedThreadId]);

  const threads = useMemo(
    () => threadsData?.getMessageThreads ?? [],
    [threadsData]
  );

  const filteredThreads = useMemo(() => {
    if (!searchTerm) return threads;
    const lowered = searchTerm.toLowerCase();
    return threads.filter((thread: any) => {
      const other = getOtherParticipant(thread, user?.id, role);
      const name = other?.displayName || "";
      return name.toLowerCase().includes(lowered);
    });
  }, [threads, searchTerm, user?.id, role]);

  const selectedThread = useMemo(() => {
    if (!selectedThreadId) return null;
    return threads.find((thread: any) => thread._id === selectedThreadId) || null;
  }, [threads, selectedThreadId]);

  useEffect(() => {
    if (!selectedThread || !user?.id || !role) {
      setMessagePermission({ allowed: true, reason: null, loading: false });
      return;
    }

    const otherParticipant = getOtherParticipant(selectedThread, user.id, role);
    const recipientId =
      otherParticipant?.userId?.toString?.() || otherParticipant?.userId;
    const recipientRole = otherParticipant?.role;
    if (!recipientId || !recipientRole) {
      setMessagePermission({ allowed: true, reason: null, loading: false });
      return;
    }

    let cancelled = false;
    setMessagePermission((prev) => ({ ...prev, loading: true }));
    fetchMessagePermission(user.id, role, recipientId, recipientRole)
      .then((permission) => {
        if (cancelled) return;
        setMessagePermission({
          allowed: permission?.allowed !== false,
          reason: permission?.reason ?? null,
          loading: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setMessagePermission({
          allowed: false,
          reason: MESSAGE_PERMISSION_CHECK_FAILED_REASON,
          loading: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [role, selectedThread, user?.id]);

  const resolveParticipantName = (
    senderId?: string | null,
    senderRole?: string | null
  ) => {
    if (!senderId || !senderRole) return "User";
    if (senderId?.toString?.() === user?.id && senderRole === role) {
      return "You";
    }
    const participant = selectedThread?.participants?.find(
      (item: any) =>
        item?.userId?.toString?.() === senderId?.toString?.() &&
        item?.role === senderRole
    );
    return participant?.displayName || "User";
  };

  const messages = useMemo(
    () => messageList,
    [messageList]
  );

  useEffect(() => {
    if (!selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0]._id);
    }
    if (threads.length === 0) {
      setSelectedThreadId(null);
    }
  }, [threads, selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId) return;
    fetchThreadMessages({ threadId: selectedThreadId, limit: MESSAGE_PAGE_SIZE });
    if (user?.id && role) {
      markThreadRead({
        variables: { threadId: selectedThreadId, userId: user.id, role },
      });
    }
  }, [selectedThreadId, fetchThreadMessages, markThreadRead, user?.id, role]);

  useEffect(() => {
    if (!selectedThreadId) return;
    const nextMessages = messagesData?.getMessageThreadMessages ?? null;
    if (nextMessages) {
      if (
        requestedThreadIdRef.current &&
        requestedThreadIdRef.current !== selectedThreadId
      ) {
        return;
      }
      if (historyLoadModeRef.current === "prepend") {
        setMessageList((prev) => {
          const existingIds = new Set(
            prev.map((message) => message?._id?.toString?.() || message?._id)
          );
          const newItems = nextMessages.filter((message: any) => {
            const id = message?._id?.toString?.() || message?._id;
            return id && !existingIds.has(id);
          });
          return [...newItems, ...prev];
        });
        if (nextMessages.length < MESSAGE_PAGE_SIZE) {
          setHasMoreHistory(false);
        }
        setIsFetchingOlder(false);
      } else {
        setMessageList(nextMessages);
        setHasMoreHistory(nextMessages.length >= MESSAGE_PAGE_SIZE);
      }
    }
  }, [messagesData, selectedThreadId]);

  useLayoutEffect(() => {
    if (!selectedThreadId) return;
    if (!messageList.length) return;
    if (pendingScrollAdjustmentRef.current) return;
    if (autoScrollThreadIdRef.current === selectedThreadId) return;
    const firstThreadId = getMessageThreadId(messageList[0]);
    if (firstThreadId && firstThreadId !== selectedThreadId) return;
    const container = threadBodyRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
      autoScrollThreadIdRef.current = selectedThreadId;
    });
  }, [messageList, selectedThreadId]);

  useLayoutEffect(() => {
    if (!pendingScrollAdjustmentRef.current) return;
    const container = threadBodyRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      const snapshot = pendingScrollAdjustmentRef.current;
      if (!snapshot) return;
      const newHeight = container.scrollHeight;
      container.scrollTop = newHeight - snapshot.height + snapshot.top;
      pendingScrollAdjustmentRef.current = null;
    });
  }, [messageList]);

  useEffect(() => {
    if (!user?.id || !role) return;
    const socket = createMessageSocket(user.id, role);

    socket.on("message:new", (payload: any) => {
      const rawThreadId =
        payload?.threadId || payload?.message?.threadId || payload?.thread?._id;
      const threadId = rawThreadId?.toString?.() || rawThreadId;
      const incomingMessage = payload?.message || null;
      refetchThreads();
      if (!threadId || threadId !== selectedThreadIdRef.current) {
        return;
      }
      if (incomingMessage) {
        const normalizedMessage = {
          ...incomingMessage,
          _id: incomingMessage?._id?.toString?.() || incomingMessage?._id,
          threadId:
            incomingMessage?.threadId?.toString?.() || incomingMessage?.threadId,
          senderId:
            incomingMessage?.senderId?.toString?.() || incomingMessage?.senderId,
        };
        setMessageList((prev) => {
          const exists = prev.some(
            (message) =>
              message?._id?.toString?.() === normalizedMessage?._id?.toString?.()
          );
          if (exists) return prev;
          return [...prev, normalizedMessage];
        });
      } else {
        loadMessages({ variables: { threadId, limit: 50 } });
      }
      const senderId =
        incomingMessage?.senderId?.toString?.() || incomingMessage?.senderId;
      if (senderId && senderId !== user.id) {
        markThreadRead({
          variables: { threadId, userId: user.id, role },
        });
      }
    });

    socket.on("message:reaction", (payload: any) => {
      const incomingMessage = payload?.message || null;
      const rawThreadId =
        payload?.threadId ||
        incomingMessage?.threadId ||
        payload?.thread?._id;
      const threadId = rawThreadId?.toString?.() || rawThreadId;
      if (!incomingMessage) return;
      if (threadId && threadId !== selectedThreadIdRef.current) {
        return;
      }
      const normalizedMessage = {
        ...incomingMessage,
        _id: incomingMessage?._id?.toString?.() || incomingMessage?._id,
        threadId:
          incomingMessage?.threadId?.toString?.() || incomingMessage?.threadId,
        senderId:
          incomingMessage?.senderId?.toString?.() || incomingMessage?.senderId,
      };
      setMessageList((prev) =>
        prev.map((message) =>
          message?._id?.toString?.() === normalizedMessage?._id?.toString?.()
            ? { ...message, ...normalizedMessage }
            : message
        )
      );
    });

    return () => {
      socket.off("message:new");
      socket.off("message:reaction");
      socket.disconnect();
    };
  }, [user?.id, role, loadMessages, markThreadRead, refetchThreads]);

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const uploaded = await uploadFiles(files);
    if (uploaded.length) {
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    }
  };

  const handleRemoveAttachment = (id?: string | null) => {
    if (!id) return;
    setPendingAttachments((prev) => prev.filter((file) => file?._id !== id));
  };

  const handleToggleReactionPicker = useCallback((messageId: string) => {
    setReactionPickerForId((prev) => (prev === messageId ? null : messageId));
  }, []);

  const handleToggleReaction = async (messageId: string, emoji: string) => {
    if (!messageId || !emoji || !user?.id || !role) return;
    try {
      const result = await toggleMessageReaction({
        variables: {
          messageId,
          emoji,
          userId: user.id,
          role,
        },
      });
      const updatedMessage = result?.data?.toggleMessageReaction;
      if (updatedMessage) {
        const normalizedMessage = {
          ...updatedMessage,
          _id: updatedMessage?._id?.toString?.() || updatedMessage?._id,
          threadId:
            updatedMessage?.threadId?.toString?.() || updatedMessage?.threadId,
          senderId:
            updatedMessage?.senderId?.toString?.() || updatedMessage?.senderId,
        };
        setMessageList((prev) =>
          prev.map((message) =>
            message?._id?.toString?.() === normalizedMessage?._id?.toString?.()
              ? { ...message, ...normalizedMessage }
              : message
          )
        );
      }
    } finally {
      setReactionPickerForId(null);
    }
  };

  const scrollToMessageById = (targetId: string) => {
    if (!targetId) return false;
    const element = document.getElementById(`message-${targetId}`);
    if (!element) return false;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMessageId(targetId);
    window.setTimeout(() => {
      setHighlightedMessageId((current) =>
        current === targetId ? null : current
      );
    }, 1600);
    return true;
  };

  const handleScrollToReply = (targetId?: string | null) => {
    if (!targetId) return;
    const didScroll = scrollToMessageById(targetId);
    if (!didScroll && selectedThreadId) {
      setScrollTargetId(targetId);
      autoScrollThreadIdRef.current = selectedThreadId;
      fetchThreadMessages({
        threadId: selectedThreadId,
        limit: MESSAGE_PAGE_SIZE * 4,
        mode: "replace",
      });
    }
  };

  useEffect(() => {
    if (!scrollTargetId) return;
    const didScroll = scrollToMessageById(scrollTargetId);
    if (didScroll) {
      setScrollTargetId(null);
    }
  }, [scrollTargetId, messageList]);

  const handleThreadScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!selectedThreadId) return;
    if (!hasMoreHistory || isFetchingOlder || messagesLoading) return;
    if (!messageList.length) return;
    const target = event.currentTarget;
    if (target.scrollTop > 120) return;
    const oldestMessage = messageList[0];
    const oldestTimestamp = parseDateValue(
      getMessageTimestampValue(oldestMessage)
    );
    if (!oldestTimestamp) return;
    fetchThreadMessages({
      threadId: selectedThreadId,
      limit: MESSAGE_PAGE_SIZE,
      before: oldestTimestamp.toISOString(),
      mode: "prepend",
    });
  };

  const messagingBlocked = Boolean(selectedThread) && !messagePermission.allowed;
  const canSend =
    !messagingBlocked && Boolean(messageDraft.trim() || pendingAttachments.length);

  const handleSendMessage = async () => {
    if (uploadingFiles || sendingMessage) return;
    if (!selectedThread || !user?.id || !role) return;
    if (messagingBlocked) {
      showError(
        messagePermission.reason || "Messaging is unavailable for this match."
      );
      return;
    }
    const trimmed = messageDraft.trim();
    if (!trimmed && pendingAttachments.length === 0) return;

    const otherParticipant = getOtherParticipant(selectedThread, user.id, role);
    if (!otherParticipant) return;

    try {
      const result = await sendMessage({
        variables: {
          input: {
            threadId: selectedThread._id,
            senderId: user.id,
            senderRole: role,
            recipientId: otherParticipant.userId,
            recipientRole: otherParticipant.role,
            body: trimmed,
            replyTo: replyTarget?._id || null,
            attachments: pendingAttachments
              .map((file) => file._id)
              .filter(Boolean),
          },
        },
      });
      setMessageDraft("");
      setPendingAttachments([]);
      setReplyTarget(null);
      const nextThread = result?.data?.sendMessage;
      const nextMessage = nextThread?.lastMessage;
      if (nextMessage) {
        const normalizedMessage = {
          ...nextMessage,
          _id: nextMessage?._id?.toString?.() || nextMessage?._id,
          threadId: nextMessage?.threadId?.toString?.() || nextMessage?.threadId,
          senderId: nextMessage?.senderId?.toString?.() || nextMessage?.senderId,
        };
        setMessageList((prev) => {
          const exists = prev.some(
            (message) =>
              message?._id?.toString?.() === normalizedMessage?._id?.toString?.()
          );
          if (exists) return prev;
          return [...prev, normalizedMessage];
        });
      }
      refetchThreads();
    } catch (error: any) {
      showError(error?.message || "Unable to send message.");
    }
  };

  const hasThreads = threads.length > 0;
  const otherParticipant = getOtherParticipant(selectedThread, user?.id, role);
  const showSidebar = !isMobile || mobileView === "list";
  const showThreadPanel = !isMobile || mobileView === "thread";
  const shellClassName = [
    "messages-shell",
    isMobile ? `is-mobile-${mobileView}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box className={`messages-page${isMobile ? ` is-mobile-${mobileView}` : ""}`}>
      <DashboardHeader title="Messages" showBackButton />
      <Box className={shellClassName}>
        {showSidebar && (
          <MessagesSidebar
            threadsLoading={threadsLoading}
            filteredThreads={filteredThreads}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedThreadId={selectedThreadId}
            onSelectThread={(threadId) => {
              setSelectedThreadId(threadId);
              if (isMobile) {
                setMobileView("thread");
              }
            }}
            userId={user?.id}
            role={role}
          />
        )}

        {showThreadPanel && (
          <Box className="messages-thread-panel">
          {!hasThreads ? (
            <Box className="messages-empty">
              <Typography variant="h6">Your inbox is empty</Typography>
              <Typography variant="body2" color="text.secondary">
                Once someone messages you, it will show up here.
              </Typography>
            </Box>
          ) : !selectedThread ? (
            <Box className="messages-empty">
              <Typography variant="h6">Select a conversation</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a thread to view the message history.
              </Typography>
            </Box>
          ) : (
            <>
              <MessagesThreadHeader
                participant={otherParticipant}
                matchId={selectedThread?.matchId}
                showBackButton={isMobile}
                onBack={() => setMobileView("list")}
              />
              <MessagesThreadBody
                messages={messages}
                messagesLoading={messagesLoading}
                isFetchingOlder={isFetchingOlder}
                highlightedMessageId={highlightedMessageId}
                reactionPickerForId={reactionPickerForId}
                onScroll={handleThreadScroll}
                threadBodyRef={threadBodyRef}
                onReply={setReplyTarget}
                onToggleReactionPicker={handleToggleReactionPicker}
                onToggleReaction={handleToggleReaction}
                onScrollToReply={handleScrollToReply}
                onImagePreview={setImagePreview}
                resolveParticipantName={resolveParticipantName}
                currentUserId={user?.id}
                role={role}
              />
              <MessageComposer
                threadId={selectedThreadId}
                messageDraft={messageDraft}
                onDraftChange={setMessageDraft}
                onSend={handleSendMessage}
                canSend={canSend}
                messagingBlocked={messagingBlocked}
                messagingBlockedReason={messagePermission.reason}
                checkingMessagePermission={messagePermission.loading}
                sendingMessage={sendingMessage}
                uploadingFiles={uploadingFiles}
                pendingAttachments={pendingAttachments}
                onRemoveAttachment={handleRemoveAttachment}
                onFileUpload={handleFileUpload}
                replyTarget={replyTarget}
                onCancelReply={() => setReplyTarget(null)}
                resolveParticipantName={resolveParticipantName}
              />
            </>
          )}
          </Box>
        )}
      </Box>
      <MessagesImageModal
        preview={imagePreview}
        onClose={() => setImagePreview(null)}
      />
    </Box>
  );
};

export default MessagesPage;
